import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, TrendingUp, Users, Save } from 'lucide-react';
import BioInput from '@/components/shared/BioInput';
import { useEnhancedBioValidation } from '@/hooks/useEnhancedBioValidation';
import { BioValidationModal } from '@/components/onboarding/BioValidationModal';
import { BioWarningModal } from '@/components/onboarding/BioWarningModal';
import { BIO_MIN_LENGTH, ValidationRules } from '@/core/BusinessRulesEngine';

export interface BioEditorValidationResult {
  bio: string;
  topics: string[];
  score: number;
  isValid: boolean;
}

export interface BioEditorRef {
  triggerValidation: () => Promise<void>;
}

interface BioEditorProps {
  initialBio: string;
  occupation?: string;
  education?: string;
  onBioChange?: (bio: string) => void;
  onValidationComplete?: (result: BioEditorValidationResult) => void;
  onValidationStart?: () => void;
  onSave?: (result: BioEditorValidationResult) => Promise<void>;
  showTopicsPreview?: boolean;
  showSaveButton?: boolean;
  showValidateButton?: boolean;
  saveButtonText?: string;
  className?: string;
}

export const BioEditor = forwardRef<BioEditorRef, BioEditorProps>(({
  initialBio,
  occupation,
  education,
  onBioChange,
  onValidationComplete,
  onValidationStart,
  onSave,
  showTopicsPreview = true,
  showSaveButton = false,
  showValidateButton = true,
  saveButtonText = 'Salvar',
  className = ''
}, ref) => {
  const [tempBio, setTempBio] = useState(initialBio);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalType, setValidationModalType] = useState<'block' | 'warning'>('block');
  const [hasValidated, setHasValidated] = useState(false);

  const { isValidating, validationResult, validateBio, clearValidation } = useEnhancedBioValidation();

  // Sync with initialBio changes
  useEffect(() => {
    setTempBio(initialBio);
  }, [initialBio]);

  const handleBioChange = (newBio: string) => {
    setTempBio(newBio);
    clearValidation();
    setHasValidated(false);
    onBioChange?.(newBio);
  };

  const handleValidate = async () => {
    if (isValidating) return;
    
    onValidationStart?.();
    const result = await validateBio(tempBio);
    
    if (!result.canProceed) {
      const modalType = ValidationRules.getBioSaveModalType(result.score);
      if (modalType === 'block') {
        setValidationModalType('block');
        setShowValidationModal(true);
        return;
      } else if (modalType === 'warning') {
        setValidationModalType('warning');
        setShowValidationModal(true);
        return;
      }
    }
    
    setHasValidated(true);
    
    // Notify parent about validation completion
    onValidationComplete?.({
      bio: tempBio,
      topics: result.topicsMentioned || [],
      score: result.score,
      isValid: result.canProceed
    });
  };

  const handleSave = async () => {
    if (isSaving || !onSave) return;
    
    setIsSaving(true);
    try {
      await onSave({
        bio: tempBio,
        topics: validationResult?.topicsMentioned || [],
        score: validationResult?.score ?? 0,
        isValid: hasValidated
      });
    } catch (error) {
      console.error('Error saving bio:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidationModalClose = () => {
    setShowValidationModal(false);
  };

  const handleValidationModalProceed = async () => {
    setShowValidationModal(false);
    setHasValidated(true);
    
    // Notify parent even if warning was bypassed
    onValidationComplete?.({
      bio: tempBio,
      topics: validationResult?.topicsMentioned || [],
      score: validationResult?.score ?? 0,
      isValid: true
    });

    if (onSave) {
      await handleSave();
    }
  };

  const canValidate = tempBio.trim().length >= BIO_MIN_LENGTH;

  // Expose triggerValidation method via ref
  useImperativeHandle(ref, () => ({
    triggerValidation: handleValidate
  }));

  return (
    <div className={`space-y-4 ${className}`}>
      <BioInput
        value={tempBio}
        onChange={handleBioChange}
        label=""
        placeholder="Conte sobre você, seus interesses, hobbies, profissão e o que mais achar importante..."
        maxLength={500}
        showQualityIndicator={false}
        showGuidance={false}
        showValidationIcon={false}
        occupation={occupation}
        education={education}
        className="min-h-[200px]"
        externalValidation={validationResult ? {
          ...validationResult,
          hasBeenValidated: !!validationResult,
          loading: isValidating
        } : undefined}
      />

      {/* ✨ Preview de Tópicos Extraídos pela IA */}
      {showTopicsPreview && hasValidated && validationResult?.topicsMentioned && validationResult.topicsMentioned.length > 0 && (
        <div className="p-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-lg border border-purple-500/30 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-purple-400" />
            <h4 className="text-sm font-semibold text-white">Tópicos Extraídos pela IA</h4>
            <Badge variant="secondary" className="ml-auto text-xs bg-purple-800/50 text-purple-200">
              {validationResult.topicsMentioned.length} tópicos
            </Badge>
          </div>
          
          <div className="space-y-3">
            {/* Explicação do valor da IA */}
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white/90 leading-relaxed">
                  Nossa IA identificou <span className="font-semibold text-purple-300">
                  {validationResult.topicsMentioned.length} tópicos únicos</span> sobre você.
                  Esses tópicos são usados para conectar você com pessoas que compartilham
                  interesses similares.
                </p>
              </div>
            </div>

            {/* Mostrar só os TOP 5 tópicos mais relevantes */}
            <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-500/20">
              <p className="text-xs text-purple-300 font-medium mb-2">
                💡 Principais tópicos detectados:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {validationResult.topicsMentioned.slice(0, 5).map((topic, idx) => (
                  <Badge 
                    key={`${topic}-${idx}`} 
                    variant="outline" 
                    className="text-xs bg-purple-900/40 border-purple-500/40 text-purple-200"
                  >
                    {topic}
                  </Badge>
                ))}
                {validationResult.topicsMentioned.length > 5 && (
                  <span className="text-xs text-purple-300/70">
                    e mais {validationResult.topicsMentioned.length - 5} tópicos
                  </span>
                )}
              </div>
            </div>

            {/* Estatística motivacional */}
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg p-3 border border-purple-500/20">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-400" />
                <p className="text-xs text-white/80">
                  <span className="font-semibold text-green-400">
                    Perfis detalhados
                  </span> recebem 3x mais matches relevantes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botões de ação - só renderiza se showValidateButton=true */}
      {showValidateButton && (
        <div className="flex justify-end gap-3">
          <Button
            onClick={hasValidated && showSaveButton ? handleSave : handleValidate}
            disabled={!canValidate || isSaving || isValidating}
            className={hasValidated 
              ? "bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white disabled:opacity-50" 
              : "bg-purple-800 hover:bg-purple-700 text-white disabled:opacity-50"
            }
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Salvando...
              </>
            ) : isValidating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Validando com IA...
              </>
            ) : hasValidated && showSaveButton ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                {saveButtonText}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Validar com IA
              </>
            )}
          </Button>
        </div>
      )}

      <BioValidationModal 
        isOpen={showValidationModal && validationModalType === 'block'}
        onClose={handleValidationModalClose}
        onProceed={handleValidationModalProceed}
        bioScore={validationResult?.score || 0}
        modalType={validationModalType}
        feedback={validationResult?.feedback}
      />

      <BioWarningModal 
        isOpen={showValidationModal && validationModalType === 'warning'}
        onClose={handleValidationModalClose}
        onProceed={handleValidationModalProceed}
        bioScore={validationResult?.score || 0}
        feedback={validationResult?.feedback}
      />
    </div>
  );
});

BioEditor.displayName = 'BioEditor';

export default BioEditor;
